import type { UseFormSetError, FieldValues, Path } from 'react-hook-form';

/**
 * API error shape returned by the backend for unique constraint violations
 * and other field-level errors.
 */
interface ApiFieldError {
  code: string;
  message: string;
  column?: string;
}

/**
 * Parses an API error (typically from a ky HTTPError) and sets the
 * corresponding field error on a React Hook Form instance.
 *
 * Returns `true` if a field-level error was set, `false` otherwise
 * (caller should fall back to a generic toast).
 *
 * Usage:
 * ```ts
 * const setApiFormError = useApiFormError(formRef.current?.setError);
 *
 * onError: (error) => {
 *   if (!setApiFormError(error)) {
 *     toast.error(error?.message || 'Something went wrong');
 *   }
 * }
 * ```
 */
export function applyApiFieldError<T extends FieldValues>(
  error: any,
  setError?: UseFormSetError<T>,
  fieldNames?: string[]
): boolean {
  if (!setError) return false;

  // The ky beforeError hook sets error.message to the API message.
  // Check for the UNIQUE_CONFLICT code pattern in the error name or
  // look for the column property that our API attaches.
  const column: string | undefined = error?.column;
  const code: string | undefined = error?.code;
  const message: string | undefined = error?.message;

  // If the error has a column and it matches a form field, set the error there
  if (column && message) {
    const fieldName = column as Path<T>;
    // Only set if the column corresponds to a known field
    if (!fieldNames || fieldNames.includes(column)) {
      setError(fieldName, { type: 'server', message });
      return true;
    }
  }

  return false;
}
